import { Props_SnippetAccordion } from "./Accordion.props";
import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { CompileHeadingToSpan } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'

const ANIMATION_DURATION = 0.3;

type AccordionRowProps = {
    accordionRow: Props_SnippetAccordion['repeatable_accordionRow'][number];
    index: number;
    isOpen: boolean;
    isDetailsOpen: boolean;
    onToggle: (index: number, event: React.MouseEvent) => void;
    onExitComplete: (index: number) => void;
};

const AccordionRow = ({ accordionRow, index, isOpen, isDetailsOpen, onToggle, onExitComplete }: AccordionRowProps) => {
    const { title, photo, photoAlt, accentColor, currentPosition } = accordionRow;
    const photoAccentClass = accentColor?.value
        ? `snippet-accordion__photo--accent--${accentColor.value}`
        : "";

    const history = [1, 2, 3, 4, 5]
        .map(i => ({
            year: (accordionRow as any)[`year${i}`]?.value as string | undefined,
            position: (accordionRow as any)[`position${i}`]?.value as string | undefined,
        }))
        .filter(h => h.year || h.position);

    return (
        <details
            key={"accordion-row-" + index}
            className={`snippet-accordion__row ${isOpen ? "snippet-accordion__row--open" : ""}`}
            open={isDetailsOpen}
        >
            <summary
                className="snippet-accordion__row-title"
                onClick={(e) => onToggle(index, e)}
            >
                {photo?.value && (
                    <img
                        className={`snippet-accordion__photo ${photoAccentClass}`.trim()}
                        src={photo.value}
                        alt={photoAlt?.value ?? ""}
                        loading="lazy"
                    />
                )}
                <div className="snippet-accordion__person">
                    <span className="snippet-accordion__icon" aria-hidden="true" />
                    <span className="snippet-accordion__name">
                        {title?.value ?? "Accordion Row " + (index + 1)}
                    </span>
                    {currentPosition?.value && (
                        <div className="snippet-accordion__position">
                            <span className="snippet-accordion__position-label">Current position:</span>
                            <span className="snippet-accordion__position-value">{currentPosition.value}</span>
                        </div>
                    )}
                </div>
            </summary>
            <AnimatePresence initial={false} onExitComplete={() => onExitComplete(index)}>
                {isOpen && (
                    <motion.div
                        className="snippet-accordion__content-container"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: ANIMATION_DURATION, ease: "easeInOut" }}
                    >
                        {history.length > 0 && (
                            <ul className="snippet-accordion__history">
                                {history.map((h, i) => (
                                    <li key={i} className="snippet-accordion__history-entry">
                                        <span className="snippet-accordion__history-year">{h.year}</span>
                                        <span className="snippet-accordion__history-position">{h.position}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </details>
    );
};

const Accordion = (props: Props_SnippetAccordion) => {
    const { accordionTitle, backgroundImage, repeatable_accordionRow } = props;
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [detailsOpenSet, setDetailsOpenSet] = useState<Set<number>>(new Set());
    const accordionTitleClass = accordionTitle.visibility?.value ? "" : "accessible-heading";

    const handleToggle = (index: number, event: React.MouseEvent) => {
        event.preventDefault();
        
        if (openIndex === index) {
            setOpenIndex(null);
        } else {
            setOpenIndex(index);
            setDetailsOpenSet(prev => new Set(prev).add(index));
        }
    };

    const handleExitComplete = (index: number) => {
        setDetailsOpenSet(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
        });
    };

    return (
        <section className="snippet-accordion">
            {backgroundImage?.src?.value && (
                <img
                    className="snippet-accordion__background"
                    src={backgroundImage.src.value}
                    alt=""
                    aria-hidden="true"
                />
            )}
            <div className="snippet-accordion__container">
                {accordionTitle?.text?.value && (
                    <h2 className={`snippet-accordion__title ${accordionTitleClass}`}>
                        {CompileHeadingToSpan(accordionTitle.text.value)}
                    </h2>
                )}

                <div className="snippet-accordion__row-container">
                    {repeatable_accordionRow.map((accordionRow, index) => (
                        <AccordionRow
                            key={"accordion-row-" + index}
                            accordionRow={accordionRow}
                            index={index}
                            isOpen={openIndex === index}
                            isDetailsOpen={detailsOpenSet.has(index)}
                            onToggle={handleToggle}
                            onExitComplete={handleExitComplete}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Accordion;
